'use client';

import { useEffect, useMemo, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import { FormTextarea } from '@/components/forms/form-textarea';
import { createBoxTypeSchema, type CreateBoxTypeFormValues } from '../schemas';
import {
  useCreateBoxTypeMutation,
  useUpdateBoxTypeMutation
} from '../services/box-types.mutations';
import type { BoxType } from '../types';

interface BoxTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boxType?: BoxType | null;
}

export function BoxTypeFormDialog({
  open,
  onOpenChange,
  boxType
}: BoxTypeFormDialogProps) {
  const t = useTranslations('boxTypes');
  const tv = useTranslations('validation');
  const isEditing = !!boxType;

  const schema = useMemo(() => createBoxTypeSchema(tv), [tv]);

  const form = useForm<CreateBoxTypeFormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      name: '',
      code: '',
      max_capacity: 1,
      description: '',
      status: 'active'
    }
  });

  const sanitizeCode = useCallback(
    (value: string) => value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase(),
    []
  );

  const watchedCode = useWatch({ control: form.control, name: 'code' });
  useEffect(() => {
    const sanitized = sanitizeCode(watchedCode ?? '');
    if (sanitized !== watchedCode) {
      form.setValue('code', sanitized, { shouldValidate: false });
    }
  }, [watchedCode, sanitizeCode, form]);

  const createMutation = useCreateBoxTypeMutation();
  const updateMutation = useUpdateBoxTypeMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (open && boxType) {
      form.reset({
        name: boxType.name,
        code: boxType.code,
        max_capacity: boxType.max_capacity,
        description: boxType.description ?? '',
        status: boxType.status
      });
    } else if (open) {
      form.reset({
        name: '',
        code: '',
        max_capacity: 1,
        description: '',
        status: 'active'
      });
    }
  }, [open, boxType, form]);

  async function onSubmit(values: CreateBoxTypeFormValues) {
    const payload = {
      ...values,
      description: values.description || undefined
    };

    if (isEditing && boxType) {
      await updateMutation.mutateAsync({ id: boxType.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    onOpenChange(false);
  }

  const statusOptions = [
    { value: 'active', label: t('statusActive') },
    { value: 'inactive', label: t('statusInactive') }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editBoxType') : t('createBoxType')}
          </DialogTitle>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            <FormInput
              control={form.control}
              name='name'
              label={t('name')}
              placeholder={t('namePlaceholder')}
              required
            />

            <FormInput
              control={form.control}
              name='code'
              label={t('code')}
              placeholder={t('codePlaceholder')}
              required
            />

            <FormInput
              control={form.control}
              name='max_capacity'
              type='number'
              label={t('maximumCapacity')}
              placeholder={t('maximumCapacityPlaceholder')}
              required
            />

            <FormTextarea
              control={form.control}
              name='description'
              label={t('description')}
              placeholder={t('descriptionPlaceholder')}
            />

            <FormSelect
              control={form.control}
              name='status'
              label={t('status')}
              options={statusOptions}
              required
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
              >
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={isPending}>
                {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
                {isEditing ? t('saveChanges') : t('create')}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
